import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, MessageCircle, Users, Calendar, DollarSign, Flag, Check, Trash2, Tag } from 'lucide-react';
import { useNotifications } from '@/context/NotificationContext';
import { Button } from '@/components/ui/button';

const typeIcons = {
  new_message: MessageCircle,
  new_match: Users,
  event_rsvp: Calendar,
  event_payment: DollarSign,
  content_flagged: Flag,
  coadmin_event_created: Calendar,
  coadmin_event_updated: Calendar,
  coadmin_event_deleted: Calendar,
  coadmin_promo_created: Tag,
};

const typeColors = {
  new_message: 'text-teal',
  new_match: 'text-pink-500',
  event_rsvp: 'text-blue-500',
  event_payment: 'text-green-600',
  content_flagged: 'text-red-500',
  coadmin_event_created: 'text-teal-deep',
  coadmin_event_updated: 'text-amber-600',
  coadmin_event_deleted: 'text-red-600',
  coadmin_promo_created: 'text-amber-700',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

export default function NotificationBell() {
  const { notifications, unreadCount, markRead, markAllRead, clearAll } = useNotifications();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleClick = (notif) => {
    if (!notif.read) markRead(notif.id);
    if (notif.link) {
      setOpen(false);
      navigate(notif.link);
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        className="relative text-text-mid hover:text-teal transition-colors"
        data-testid="notification-bell"
      >
        <Bell size={20} />
        {unreadCount > 0 && (
          <span
            className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-red-500 rounded-full flex items-center justify-center text-white text-[10px] font-bold px-1"
            data-testid="notification-badge"
          >
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className="absolute right-0 top-10 w-80 sm:w-96 bg-white rounded-2xl shadow-xl border border-border-subtle z-50 overflow-hidden"
          data-testid="notification-dropdown"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border-subtle gap-2">
            <h3 className="font-semibold text-navy text-sm">Notifications</h3>
            <div className="flex items-center gap-1">
              {unreadCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={markAllRead}
                  className="text-xs text-teal hover:text-teal-deep h-auto py-1"
                  data-testid="mark-all-read-btn"
                >
                  <Check size={12} className="mr-1" /> Mark all read
                </Button>
              )}
              {notifications.length > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (window.confirm('Clear all notifications? This cannot be undone.')) clearAll();
                  }}
                  className="text-xs text-red-600 hover:text-red-700 h-auto py-1"
                  data-testid="clear-all-notifications-btn"
                >
                  <Trash2 size={12} className="mr-1" /> Clear
                </Button>
              )}
            </div>
          </div>

          {/* List */}
          <div className="max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="py-12 text-center">
                <Bell size={28} className="mx-auto text-text-light mb-2 opacity-40" />
                <p className="text-sm text-text-mid">No notifications yet</p>
              </div>
            ) : (
              notifications.slice(0, 15).map((notif) => {
                const Icon = typeIcons[notif.type] || Bell;
                const iconColor = typeColors[notif.type] || 'text-text-mid';
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-cream/50 transition-colors border-b border-border-subtle last:border-0 ${
                      !notif.read ? 'bg-teal/5' : ''
                    }`}
                    data-testid={`notification-item-${notif.id}`}
                  >
                    <div className={`mt-0.5 flex-shrink-0 ${iconColor}`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm ${!notif.read ? 'font-semibold text-navy' : 'text-text-mid'}`}>
                        {notif.body}
                      </p>
                      <p className="text-xs text-text-light mt-0.5">{timeAgo(notif.created_at)}</p>
                    </div>
                    {!notif.read && (
                      <div className="w-2 h-2 rounded-full bg-teal flex-shrink-0 mt-1.5" />
                    )}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
